import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Button } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import APICONST from '../../constants';
import BridgeMarks from '../../components/BridgeMarks';
import { IMPORT_DISMISSED_KEY } from '../../components/SafetyloopImport/constants';
import { SsoWrapper } from './styles';

// Inbound SSO landing for the SafetyLoop -> ProjectLoop bridge. Reads the signed
// handoff token from the URL, exchanges it at POST /api/auth/bridge for a normal
// ProjectLoop session, stores the same cookies the Login page sets, then forces a
// full document reload so AppRoutes re-evaluates the auth cookie and selects the
// protected route tree (navigate() would stay inside the public tree).
const setSessionCookies = (user, authToken) => {
  Cookies.set('auth-token', authToken, { expires: 1 });
  Cookies.set('company_id', user.company.unique_reference_id, { expires: 1 });
  Cookies.set('company_name', user.company.name, { expires: 1 });
  Cookies.set('user', user.full_name, { expires: 1 });
  Cookies.set('userInfo', JSON.stringify(user), { expires: 1 });
  Cookies.set('user_id', user.unique_reference_id, { expires: 1 });
};

const STEPS = [
  'Verifying your SafetyLoop session',
  'Matching your ProjectLoop account',
  'Preparing your workspace'
];
// While the request is in flight the checklist advances on these timers, then
// holds on the last step until the API actually answers. Once it has, any
// remaining steps tick off quickly before the redirect.
const STEP_DELAYS = [900, 1100];
const CATCH_UP_DELAY = 220;
const REDIRECT_DELAY = 450;
const PROGRESS = [18, 46, 74, 100];

// Only same-origin paths: the redirect param arrives in a link from elsewhere.
const safeRedirect = (value) =>
  value && value.startsWith('/') && !value.startsWith('//') ? value : '/dashboard';

const stepState = (index, stage) => {
  if (index < stage) return 'is-done';
  if (index === stage) return 'is-active';
  return '';
};

const Sso = () => {
  const [error, setError] = useState(null);
  const [signedIn, setSignedIn] = useState(false);
  const [stage, setStage] = useState(0);
  const redirectRef = useRef('/dashboard');
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    redirectRef.current = safeRedirect(params.get('redirect'));

    if (!token) {
      setError('Missing ProjectLoop SSO token.');
      return;
    }

    // Strip the token from the URL immediately (browser history + referrer hygiene).
    window.history.replaceState({}, document.title, '/sso');

    axios
      .post(`${APICONST.BASE_URL}auth/bridge`, { token })
      .then((r) => {
        if (r.status === 200 && r.data && r.data.auth_token) {
          setSessionCookies(r.data.user, r.data.auth_token);
          // A fresh sign-in may offer the SafetyLoop import again.
          try {
            sessionStorage.removeItem(IMPORT_DISMISSED_KEY);
          } catch (e) {
            // Storage blocked; the wizard just stays dismissed for this tab.
          }
          setSignedIn(true);
        } else {
          setError('Could not complete ProjectLoop sign-in.');
        }
      })
      .catch((err) => setError(err.response?.data?.error || 'ProjectLoop SSO sign-in failed.'));
  }, []);

  useEffect(() => {
    if (error) return undefined;
    if (stage >= STEPS.length) {
      // Full reload so the router re-selects protectedRoutes from the cookie.
      const timer = setTimeout(() => window.location.replace(redirectRef.current), REDIRECT_DELAY);
      return () => clearTimeout(timer);
    }
    if (!signedIn && stage >= STEP_DELAYS.length) return undefined;

    const timer = setTimeout(
      () => setStage((current) => current + 1),
      signedIn ? CATCH_UP_DELAY : STEP_DELAYS[stage]
    );
    return () => clearTimeout(timer);
  }, [stage, signedIn, error]);

  return (
    <SsoWrapper>
      <div className="sso-card" role="status" aria-live="polite">
        {error ? (
          <>
            <span className="sso-error-icon">
              <CloseOutlined />
            </span>
            <h1 className="sso-title">We couldn&apos;t sign you in</h1>
            <p className="sso-subtitle">{error}</p>
            <div className="sso-actions">
              <Button type="primary" href="/login">
                Go to login
              </Button>
            </div>
          </>
        ) : (
          <>
            <BridgeMarks size={56} animated />
            <h1 className="sso-title">
              {stage >= STEPS.length ? 'You’re in!' : 'Signing you in to ProjectLoop'}
            </h1>
            <p className="sso-subtitle">
              Bringing your SafetyLoop account across. This only takes a moment.
            </p>
            <ul className="sso-steps">
              {STEPS.map((label, index) => (
                <li key={label} className={`sso-step ${stepState(index, stage)}`}>
                  <span className="sso-step-icon">{index < stage && <CheckOutlined />}</span>
                  {label}
                </li>
              ))}
            </ul>
            <div className="sso-progress">
              <div className="sso-progress-fill" style={{ width: `${PROGRESS[stage]}%` }} />
            </div>
          </>
        )}
      </div>
    </SsoWrapper>
  );
};

export default Sso;
